import * as React from 'react';
import styled from 'styled-components';
import {
  alignItems,
  border,
  borderRadius,
  boxShadow,
  color,
  display,
  height,
  justifyContent,
  maxWidth,
  minWidth,
  space,
  width,
} from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { StyledSystemProps } from '../../theme/StyledSystemProps';
import { Loader, Spacer } from './index';

interface ButtonProps extends StyledSystemProps {
  id?: string;
  text?: string;
  // Standard button attributes, needed by icon-only buttons that have no visible label.
  title?: string;
  'aria-label'?: string;
  size?: string;
  color?: string;
  hover?: boolean;
  isIcon?: boolean;
  minimal?: boolean;
  outline?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  isLoading?: boolean;
  onClick?: (e?: any) => void;
  icon?: React.ReactNode;
  style?: object;
  type: 'button' | 'submit' | 'reset';
  isWeb?: boolean;
  ref?: any;
}

const StyledButton = styled.button`
  ${space};
  ${width};
  ${color};
  ${border};
  ${height};
  ${(props: ButtonProps) => (props.hidden ? 'none' : display)};
  ${maxWidth};
  ${alignItems};
  ${borderRadius};
  ${justifyContent};
  ${(props: ButtonProps) => !props.isIcon && minWidth};
  ${(props: ButtonProps) => !props.minimal && boxShadow};
  cursor: pointer;
  &:hover {
    box-shadow: ${(props: ButtonProps) =>
      props.bg === 'transparent' || props.minimal ? 'none' : '0 1px 4px rgba(50,50,93,0.4)'};
    background: ${(props: ButtonProps) => props.minimal && 'rgba(50,50,93,0.05)'};
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transform: ${(props: ButtonProps) => props.hover && 'translateY(-1px)'};
    cursor: pointer;
  }
  &:disabled {
    opacity: 0.6;
  }

  i,
  i > svg {
    height: ${themeGet('space[2]', '18px')};
    width: ${themeGet('space[2]', '18px')};
  }
  font-size: ${(props: ButtonProps) => !props.isWeb && '0.9em'};
  font-weight: ${(props: ButtonProps) => !props.isWeb && 600};
  letter-spacing: ${(props: ButtonProps) => !props.isWeb && '0.1em'};
`;

const Button: React.FC<ButtonProps> = props => {
  return (
    <StyledButton disabled={props.isLoading} {...props}>
      {props.icon && <i>{props.icon}</i>}
      {props.icon && !props.isIcon && props.text && <Spacer spacing={4} vertical={true} />}
      {props.isLoading ? <Loader width={20} color={props.color} height={20} /> : !props.isIcon && props.text}
    </StyledButton>
  );
};

Button.defaultProps = {
  alignItems: 'center',
  icon: null,
  size: 'md',
  bg: 'primary',
  color: '#FFF',
  display: 'flex',
  width: '100%',
  outline: false,
  height: '40px',
  minimal: false,
  text: 'Button',
  isLoading: false,
  p: '0.25em 1.2em',
  minWidth: '150px',
  onClick: () => null,
  borderRadius: '6px',
  justifyContent: 'center',
  boxShadow: '0 1px 3px rgba(50,50,93,0.2)',
};

export default Button;
